import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import SelectField from 'Shared/form/SelectField';
import * as R from 'ramda';
import useGetReferralMedication from '../../hooks/useGetReferralMedication';
import { useFormikContext } from 'formik';
import { dateOnlySlashesFormat, formatUtcDate } from 'App/services/DateService';
import TherigyInfoMessage from '../TherigyInfoMessage/TherigyInfoMessage';
import useFetchReferralQuery from 'App/hooks/useFetchReferralQuery';
import { referralActivityPropType, referralPropType } from './types';

const activityInProgressStatus = {
    0: ['Pending', 'Created', 'New'],
    1: ['Pending', 'Created', 'New'],
    2: ['Pending', 'Pending Submission', 'Created', 'Submitted', 'New'],
    3: ['Pending', 'Pending Submission', 'Created', 'Submitted'],
    4: ['New', 'Pending'],
    5: ['New', 'Pending Submission', 'Submitted'],
};

const propTypes = {
    ...referralPropType,
    ...referralActivityPropType,
    activitiesMap: PropTypes.objectOf(
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.shape({})]))
    ).isRequired,
    validPriorAuthorizations: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            statusDate: PropTypes.string,
            medications: PropTypes.arrayOf(
                PropTypes.shape({
                    medication: PropTypes.shape({
                        name: PropTypes.string,
                    }),
                })
            ),
        })
    ).isRequired,
};

const defaultProps = {};

const getMedicationOption = (medication) => ({
    id: medication.medicationId,
    name: [medication?.medication?.name, medication?.medication?.therapy?.name].filter((item) => !!item).join(' - '),
});

const getUniqueMedicationOptions = R.uniqBy(R.prop('id'));

const ReferralMedicationSelect = ({ patientId, referralId, activities, activitiesMap, validPriorAuthorizations }) => {
    const { data: medications } = useGetReferralMedication(patientId, referralId);
    const { values, setFieldValue, setErrors } = useFormikContext();
    const { data: referral } = useFetchReferralQuery(patientId, referralId);

    /**
     * TODO: do not use `useEffect` to set values after initial render. Just set the value on the event, (button
     * click, for example), or set all the initial values before the first render.
     */
    useEffect(() => {
        setFieldValue('paActivityId', null);
        const type = values?.type?.value;
        const activityInProgress = activities?.find((it) => {
            return it.type === Number(type) && activityInProgressStatus[type].includes(it.status);
        });
        setFieldValue('showReferralWarningMessage', !!activityInProgress?.id);
        if (type !== null) {
            setFieldValue(
                'medication',
                getUniqueMedicationOptions(referral?.medications?.map(getMedicationOption) || [])
            );
        } else {
            setFieldValue('medication', []);
            setFieldValue('showReferralWarningMessage', false);
        }
        setTimeout(() => {
            setErrors({});
        }, 0);
    }, [activities, referral?.medications, setErrors, setFieldValue, values?.type?.value]);

    const paOptions = useMemo(() => {
        return validPriorAuthorizations.map((it) => {
            return {
                name: `PA denied on ${formatUtcDate({
                    date: it.statusDate,
                    format: dateOnlySlashesFormat,
                })} (${it.medications?.map((i) => i?.medication?.name).join(', ')})`,
                id: it.id,
                raw: it,
            };
        });
    }, [validPriorAuthorizations]);

    const medicationList = useMemo(() => {
        if (!(values?.type?.value >= 0) || !activitiesMap || !medications) {
            return [];
        }
        const medList = medications.filter((m) => {
            return activitiesMap[values?.type?.value].indexOf(m.medicationId) === -1;
        });
        return getUniqueMedicationOptions(medList.map(getMedicationOption));
    }, [activitiesMap, medications, values?.type?.value]);
    if (values?.showReferralWarningMessage) {
        return (
            <div>
                <TherigyInfoMessage
                    message={`There is already a ${values?.type?.label} in process for this referral.`}
                />
                <div className="info-add-activity-modal">
                    <span>
                        <b>Are you sure you want to continue adding another?</b>
                    </span>
                </div>
            </div>
        );
    }

    if (!medicationList.length) {
        return null;
    }

    return (
        <>
            {values.type?.value === 3 && (
                <div>
                    <SelectField
                        options={paOptions}
                        isClearable
                        required
                        label="Upheld Prior Authorization to Appeal"
                        fieldName="paActivityId"
                        getOptionValue={R.prop('id')}
                        getOptionLabel={R.prop('name')}
                    />
                </div>
            )}
            <div>
                <SelectField
                    options={medicationList}
                    isClearable
                    required
                    label="Medication"
                    fieldName="medication"
                    getOptionValue={R.prop('id')}
                    getOptionLabel={R.prop('name')}
                    isMulti
                />
            </div>
        </>
    );
};

ReferralMedicationSelect.propTypes = propTypes;
ReferralMedicationSelect.defaultProps = defaultProps;

export default ReferralMedicationSelect;
